// React
import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import { updateInvoiceExtractionSettingsAPI } from "services/api/settings/invoiceExtraction/api";
import {
  DEFAULT_GOOGLE_LOCATION,
  EXPENSE_TYPES_PLACEHOLDER,
  InvoiceExtractionPipeline,
  InvoiceExtractionSettingsFormValues,
} from "services/api/settings/invoiceExtraction/types";
import { useHandleTextFieldChange } from "services/forms/hooks";

// UI
import { Alert } from "ui/components/Alert";
import { Badge } from "ui/components/Badge";
import { Button } from "ui/components/Button/Button";
import { CardSelect } from "ui/components/CardSelect";
import { TextArea } from "ui/components/TextField/TextArea";
import { TextField } from "ui/components/TextField/TextField";

import {
  PIPELINE_CARD_OPTIONS,
  getPipelineChoice,
} from "ui/modules/settings/pages/ApplicationSettingsPage/consts";
import ExtractionFlow from "../ExtractionFlow/ExtractionFlow";
import { getFormValues } from "./consts";
import { InvoiceExtractionEditCardProps } from "./types";

/**
 * Izmjena postavki čitanja računa. Na ekranu stoji samo ono što odabrani način stvarno koristi —
 * model je uvijek jedan, a podaci o Document AI-ju se traže tek kad on čita stranicu. Upute vrijede
 * na oba načina i moraju zadržati oznaku za popis vrsta troška, inače bi model izmišljao kategorije
 * izvan šifrarnika, pa se spremanje tada ne nudi.
 */
const InvoiceExtractionEditCard: React.FC<InvoiceExtractionEditCardProps> = (props) => {
  const { show, settings, onSuccess, onClose } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<InvoiceExtractionSettingsFormValues>(
    getFormValues(settings)
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);

  useEffect(() => {
    if (!show) return;

    setFormValues(getFormValues(settings));
  }, [show, settings]);

  const handlePipelineSelect = useCallback((pipeline: string) => {
    setFormValues((previous) => ({ ...previous, pipeline }));
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);

    try {
      onSuccess(await updateInvoiceExtractionSettingsAPI(formValues, t));
    } catch {
      // greška se prikazuje kroz snackbar
    }

    setIsSaving(false);
  }, [formValues, onSuccess, t]);

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      handleSave();
    },
    [handleSave]
  );

  const choice = getPipelineChoice(formValues.pipeline);
  const isOcrPipeline = formValues.pipeline === InvoiceExtractionPipeline.OCR;
  const hasPlaceholder = formValues.prompt.includes(EXPENSE_TYPES_PLACEHOLDER);

  // Document AI radi tek kad ima sve troje; ključ može doći iz unosa ili već biti spremljen.
  const hasGoogleSettings =
    !!formValues.googleProjectId &&
    !!formValues.googleProcessorId &&
    (settings.hasGoogleCredentials || !!formValues.googleCredentialsJson);

  return (
    <form noValidate onSubmit={handleSubmit}>
      <div className="px-4 pt-5 pb-24 flex flex-col gap-5">
        {!settings.isConfigured && (
          <Alert
            variant="danger"
            title="Nedostaje API ključ"
            message="Ključ modela nije postavljen na poslužitelju, pa se računi ne mogu čitati bez obzira na ove postavke."
          />
        )}

        <div className="flex flex-col gap-3">
          <p className="text-sm font-semibold text-slate-700">Tko čita stranicu</p>

          <CardSelect
            options={PIPELINE_CARD_OPTIONS}
            value={choice.value}
            onChange={handlePipelineSelect}
            columns={2}
          />

          <ExtractionFlow steps={choice.steps} />
        </div>

        {isOcrPipeline && !hasGoogleSettings && (
          <Alert
            variant="warning"
            title="Document AI još nije podešen"
            message="Dok projekt, procesor i ključ servisnog računa ne budu upisani, računi se čitaju tako da model gleda stranicu."
          />
        )}

        <div className="flex flex-col gap-3">
          <p className="text-sm font-semibold text-slate-700">Model</p>

          <TextField
            type="text"
            name={isOcrPipeline ? "ocrModel" : "model"}
            label={choice.modelLabel}
            value={isOcrPipeline ? formValues.ocrModel : formValues.model}
            onChange={handleTextFieldChange}
            placeholder={isOcrPipeline ? settings.defaultOcrModel : settings.defaultModel}
          />

          <TextArea
            name="prompt"
            label="Upute modelu"
            value={formValues.prompt}
            onChange={handleTextFieldChange}
            error={
              hasPlaceholder ? "" : `Upute moraju sadržavati oznaku ${EXPENSE_TYPES_PLACEHOLDER}`
            }
            rows={16}
            cssClassName="font-mono text-xs"
          />

          <ul className="common-list text-sm">
            <li>Iste upute vrijede na oba načina čitanja — mijenja se samo tko čita stranicu.</li>
            <li>
              Na mjesto oznake {EXPENSE_TYPES_PLACEHOLDER} ubacuje se popis vrsta troška iz
              šifrarnika, da model bira postojeću vrstu umjesto da izmišlja svoju.
            </li>
            <li>Prazno polje znači da vrijedi ugrađena vrijednost.</li>
          </ul>
        </div>

        {isOcrPipeline && (
          <div className="flex flex-col gap-3">
            <p className="text-sm font-semibold text-slate-700">Google Document AI</p>

            {!settings.canStoreCredentials && (
              <Alert
                variant="warning"
                title="Ključ se ne može spremiti"
                message="Na poslužitelju nije podešen ključ za šifriranje, pa se ključ servisnog računa ne može sigurno pohraniti."
              />
            )}

            <div className="flex gap-4">
              <div className="flex-1">
                <TextField
                  type="text"
                  name="googleProjectId"
                  label="Project ID"
                  value={formValues.googleProjectId}
                  onChange={handleTextFieldChange}
                  placeholder="npr. autobiz-ocr"
                />
              </div>

              <div className="flex-1">
                <TextField
                  type="text"
                  name="googleProcessorId"
                  label="Processor ID"
                  value={formValues.googleProcessorId}
                  onChange={handleTextFieldChange}
                  placeholder="npr. a1b2c3d4e5f6"
                />
              </div>

              <div className="flex-1">
                <TextField
                  type="text"
                  name="googleLocation"
                  label="Regija"
                  value={formValues.googleLocation}
                  onChange={handleTextFieldChange}
                  placeholder={DEFAULT_GOOGLE_LOCATION}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Ključ servisnog računa:</span>

              {settings.hasGoogleCredentials ? (
                <Badge
                  text={settings.googleServiceAccount || "Postavljen"}
                  variant="success"
                  height="small"
                  showDot
                />
              ) : (
                <Badge text="Nije postavljen" variant="secondary" height="small" showDot />
              )}
            </div>

            <TextArea
              name="googleCredentialsJson"
              label="Novi ključ servisnog računa"
              value={formValues.googleCredentialsJson}
              onChange={handleTextFieldChange}
              placeholder='Zalijepite JSON ključ servisnog računa, npr. { "type": "service_account", ... }'
              rows={6}
              cssClassName="font-mono text-xs"
              disabled={!settings.canStoreCredentials}
            />

            <ul className="common-list text-sm">
              <li>Ključ se sprema šifrirano i nikad se ne prikazuje natrag.</li>
              <li>Ostavite li polje prazno, spremljeni ključ ostaje nepromijenjen.</li>
              <li>Servisni račun mora imati pravo „Document AI API User" na odabranom procesoru.</li>
            </ul>
          </div>
        )}
      </div>

      <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
        <Button variant="outlined" children={t("admin.close")} onClick={onClose} />
        <Button
          variant="primary"
          children={t("admin.save")}
          type="submit"
          disabled={isSaving || !hasPlaceholder}
        />
      </div>
    </form>
  );
};

export default memo(InvoiceExtractionEditCard);
